// Session 53 · Photo dropzone for the onboarding wizard.
//
// Drag-and-drop OR click-to-pick. Reads the file → base64 → POSTs to
// /api/owner/uploads/photo → object storage. Reports the resulting URL
// via onChange. Supports:
//   * mode="single"  — one image; onChange(url)
//   * mode="multiple" — array of images; onChange([url1, url2, ...])
//
// Accepts .jpg/.png/.webp up to 6 MB (server enforces).
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function uploadOne(file) {
  if (file.size > 6 * 1024 * 1024) throw new Error(`${file.name} exceeds 6 MB`);
  const dataUrl = await fileToDataUrl(file);
  const r = await api.post("/owner/uploads/photo", { data_url: dataUrl });
  return r.data?.url;
}

export default function PhotoDropzone({ value, onChange, mode = "single", label,
                                            testid, aspect = "16/9", maxCount = 8 }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  // Session 59.5 · P0 · Memoize `items` so the useCallback below has a
  // stable dependency. Previously `items` was recomputed on every render
  // via a conditional expression, which meant `handleFiles` was recreated
  // on every render and any child prop-typing on it tripped renders too.
  const items = useMemo(
    () => (mode === "multiple"
      ? (Array.isArray(value) ? value : [])
      : (value ? [value] : [])),
    [mode, value],
  );

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      if (mode === "single") {
        const url = await uploadOne(files[0]);
        onChange(url);
        toast.success("Photo uploaded");
      } else {
        const urls = [];
        for (const f of Array.from(files).slice(0, maxCount - items.length)) {
          const url = await uploadOne(f);
          if (url) urls.push(url);
        }
        onChange([...items, ...urls]);
        toast.success(`${urls.length} photo${urls.length === 1 ? "" : "s"} uploaded`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [mode, items, maxCount, onChange]);

  const removeAt = (idx) => {
    if (mode === "single") {
      onChange("");
    } else {
      const next = items.slice();
      next.splice(idx, 1);
      onChange(next);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div data-testid={testid}>
      {label && (
        <div style={{
          fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
          color: "rgba(255,255,255,0.65)", marginBottom: 6,
        }}>{label}</div>
      )}
      {items.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: mode === "single" ? "1fr" : "repeat(auto-fill, minmax(140px, 1fr))",
          gap: 8, marginBottom: 10,
        }}>
          {items.map((url, i) => (
            <div key={url + i} data-testid={`${testid}-thumb-${i}`}
              style={{
                position: "relative",
                aspectRatio: aspect,
                overflow: "hidden", borderRadius: 8,
                border: "1px solid rgba(212,168,75,0.28)",
                background: "var(--navy-dark, #0f1729)",
              }}>
              <img src={url} alt="Uploaded photo" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
              <button type="button" onClick={() => removeAt(i)}
                data-testid={`${testid}-remove-${i}`}
                aria-label="Remove photo"
                style={{
                  position: "absolute", top: 6, right: 6,
                  width: 28, height: 28, borderRadius: "50%",
                  background: "rgba(15,23,41,0.85)", color: "#fff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  fontSize: 14, cursor: "pointer", display: "flex",
                  alignItems: "center", justifyContent: "center",
                }}>×</button>
            </div>
          ))}
        </div>
      )}
      {(mode === "multiple" || items.length === 0) && (
        <button
          type="button"
          onClick={openPicker}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          data-testid={`${testid}-dropzone`}
          disabled={uploading || (mode === "multiple" && items.length >= maxCount)}
          style={{
            width: "100%", padding: "20px 18px",
            border: `1.5px dashed ${dragging ? "#B8913A" : "rgba(212,168,75,0.35)"}`,
            background: dragging ? "rgba(184,145,58,0.10)" : "rgba(255,255,255,0.02)",
            borderRadius: 10, cursor: uploading ? "wait" : "pointer",
            color: "rgba(255,255,255,0.75)", fontSize: 13,
            transition: "all 0.15s ease",
            textAlign: "center", opacity: uploading ? 0.6 : 1,
          }}>
          {uploading ? (
            "Uploading…"
          ) : (
            <>
              <div style={{ fontSize: 22, marginBottom: 4 }}>📸</div>
              <div style={{ fontWeight: 600, color: "#fff" }}>
                {items.length === 0 ? "Drag a photo or click to upload" :
                 mode === "multiple" ? `Add more (${items.length}/${maxCount})` : "Replace photo"}
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 3 }}>
                JPG, PNG, or WebP · up to 6 MB
              </div>
            </>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple={mode === "multiple"}
        onChange={(e) => handleFiles(e.target.files)}
        style={{ display: "none" }}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
